<!-- Ellipsis dropdown with searchable, lazy-loaded historical context list. -->
<template>
  <div class="context-history" ref="wrapper">
    <button
      class="button is-small context-history__trigger"
      @click="toggle"
      title="More contexts"
    >
      <span class="icon is-small">
        <FontAwesomeIcon :icon="faEllipsis" />
      </span>
    </button>

    <div v-if="isOpen" class="context-history__dropdown">
      <div class="context-history__search">
        <input
          ref="searchInput"
          type="text"
          v-model="query"
          class="input is-small"
          placeholder="Search contexts..."
          @input="onSearch"
        >
      </div>
      <div class="context-history__list" ref="listEl" @scroll="onScroll">
        <div
          v-for="ctx in uniqueContexts"
          :key="ctx.id"
          class="context-history__item"
        >
          <button class="context-history__label-btn" @click="selectContext(ctx)">
            <span class="context-history__label">{{ ctx.display_label }}</span>
            <span class="context-history__date">{{ formatRelativeDate(ctx.started_at) }}</span>
          </button>
          <button
            class="context-history__begin-btn"
            @click.stop="$emit('begin-in-past', ctx)"
            title="Begin in past with this context"
          >
            <span class="icon is-small">
              <FontAwesomeIcon :icon="faClock" />
            </span>
          </button>
        </div>
        <p v-if="!loading && !uniqueContexts.length" class="context-history__empty">No contexts found</p>
        <p v-if="loading" class="context-history__loading">Loading...</p>
      </div>
      <div class="context-history__footer">
        <button class="context-history__log-btn" @click="$emit('log-past'); isOpen = false">
          <span class="icon is-small"><FontAwesomeIcon :icon="faClock" /></span>
          <span>Log past context</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faEllipsis, faClock } from '@fortawesome/free-solid-svg-icons'

const emit = defineEmits(['select', 'begin-in-past', 'log-past'])

const store = useBlocksStore()
const wrapper = ref(null)
const searchInput = ref(null)
const listEl = ref(null)

const isOpen = ref(false)
const query = ref('')
const results = ref([])
const loading = ref(false)
const hasMore = ref(true)
const PAGE_SIZE = 20

const uniqueContexts = computed(() => {
  const seen = new Set()
  return results.value.filter(ctx => {
    if (!ctx.display_label || seen.has(ctx.display_label)) return false
    seen.add(ctx.display_label)
    return true
  })
})

let searchTimeout = null

async function toggle() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    query.value = ''
    results.value = []
    hasMore.value = true
    await loadMore()
    await nextTick()
    searchInput.value?.focus()
  }
}

function onSearch() {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(async () => {
    results.value = []
    hasMore.value = true
    await loadMore()
  }, 200)
}

async function loadMore() {
  if (loading.value || !hasMore.value) return
  loading.value = true
  const data = await store.searchContexts({
    q: query.value,
    offset: results.value.length,
    limit: PAGE_SIZE,
  })
  if (data.length < PAGE_SIZE) hasMore.value = false
  results.value.push(...data)
  loading.value = false
}

function onScroll() {
  if (!listEl.value) return
  const el = listEl.value
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
    loadMore()
  }
}

function selectContext(ctx) {
  emit('select', ctx)
  isOpen.value = false
}

function formatRelativeDate(dateStr) {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now - d
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function onClickOutside(e) {
  if (wrapper.value && !wrapper.value.contains(e.target)) {
    isOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', onClickOutside, true))
onUnmounted(() => document.removeEventListener('click', onClickOutside, true))
</script>

<style scoped>
.context-history {
  position: relative;
}

.context-history__trigger {
  background: transparent;
  border-color: rgba(213, 99, 45, 0.12);
  color: hsla(19deg, 67%, 34%, 0.92);
}

.context-history__trigger:hover {
  background: rgba(213, 99, 45, 0.13);
  border-color: rgba(213, 99, 45, 0.12);
  color: hsla(19deg, 67%, 34%, 0.92);
}

.context-history__dropdown {
  position: absolute;
  bottom: 100%;
  right: 0;
  width: 280px;
  max-height: 320px;
  margin-bottom: 6px;
  background: var(--bulma-scheme-main);
  border: 1px solid var(--bulma-border);
  border-radius: 6px;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  z-index: 40;
}

.context-history__search {
  padding: 8px;
  border-bottom: 1px solid var(--bulma-border);
}

.context-history__list {
  flex: 1;
  overflow-y: auto;
  max-height: 240px;
}

.context-history__item {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  font-size: 0.85rem;
  color: var(--bulma-text);
  transition: background 0.1s;
}

.context-history__item:hover {
  background: rgba(213, 99, 45, 0.08);
}

.context-history__label-btn {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 0 8px 12px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-size: inherit;
  color: inherit;
}

.context-history__begin-btn {
  flex-shrink: 0;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px 10px;
  color: var(--bulma-text-weak);
  border-radius: 4px;
  transition: color 0.1s, background 0.1s;
}

.context-history__begin-btn:hover {
  color: var(--timist-context-orange, #d5632d);
  background: rgba(213, 99, 45, 0.1);
}

.context-history__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.context-history__date {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--bulma-text-weak);
}

.context-history__empty,
.context-history__loading {
  padding: 12px;
  text-align: center;
  font-size: 0.85rem;
  color: var(--bulma-text-weak);
}

.context-history__footer {
  border-top: 1px solid var(--bulma-border);
  padding: 4px;
}

.context-history__log-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--bulma-text-weak);
  border-radius: 4px;
  transition: color 0.1s, background 0.1s;
}

.context-history__log-btn:hover {
  color: var(--timist-context-orange, #d5632d);
  background: rgba(213, 99, 45, 0.08);
}

@media (prefers-color-scheme: dark) {
  .context-history__trigger {
    background: transparent;
    border-color: rgba(213, 99, 45, 0.08);
    color: hsla(19deg, 72%, 72%, 0.88);
  }

  .context-history__trigger:hover {
    background: rgba(213, 99, 45, 0.11);
    border-color: rgba(213, 99, 45, 0.08);
    color: hsla(19deg, 72%, 72%, 0.88);
  }
}
</style>
